import { User } from '@prisma/client'
import { Form, Select, Switch, Typography } from 'antd'
import React from 'react'
import { CommonClient } from '~/agents/common/client'
import { OnboardingValues } from '~/agents/salesAssistant/common'
import { FeatureFormConfigurationClient } from '~/features/formConfiguration/client'
import { TitlePrimary } from '~/features/shared/components'
import { StepProps } from '../types'

type Props = {
  user: User
} & StepProps

export const StepDailyFollowUp: React.FC<Props> = ({ user, ...props }) => {
  const configuration = { timezone: user.timezone }

  const firstFollowUpOptions = Array.from({ length: 7 }, (_, i) => i + 2) // 2–8
  const secondFollowUpOptions = Array.from({ length: 7 }, (_, i) => i + 4) // 4–10

  const ensureValues = (values: OnboardingValues): OnboardingValues => {
    if (values.followUp.first === props.values.followUp.first) {
      return { ...values }
    }

    let valueFollowUpSecond = values.followUp.second

    if (values.followUp.first > valueFollowUpSecond - 2) {
      valueFollowUpSecond = values.followUp.first + 2
    }

    return {
      ...values,
      followUp: {
        ...values.followUp,
        first: values.followUp.first,
        second: valueFollowUpSecond,
      },
    }
  }

  return (
    <>
      <CommonClient.Onboarding.Step
        {...props}
        onChange={values => props.onChange(ensureValues(values))}
      >
        <TitlePrimary level={4}>
          Should I follow-up with leads that haven't responded to our initial
          contact?
        </TitlePrimary>

        <Form.Item
          name={['followUp', 'activated']}
          rules={[{ required: true }]}
        >
          <Switch checkedChildren="Yes" unCheckedChildren="No" />
        </Form.Item>

        <Form.Item shouldUpdate={true}>
          {options => {
            const isActive = options.getFieldValue(['followUp', 'activated'])

            if (!isActive) {
              return <></>
            }

            return (
              <>
                <div className="mb-10">
                  <Typography.Text>
                    ✨ Great, everyday I'll follow-up with leads that haven't
                    responded to our initial contact
                  </Typography.Text>
                </div>
                <Form.Item
                  name={['followUp', 'rule']}
                  label="Choose a time that works best for you"
                  rules={[{ required: true }]}
                  getValueFromEvent={items => items[0]}
                >
                  <FeatureFormConfigurationClient.RRulePicker
                    type="DAILY"
                    formValuesConfiguration={configuration}
                  />
                </Form.Item>

                <Form.Item
                  name={['followUp', 'first']}
                  label="How many days should I wait for the first follow-up?"
                >
                  <Select variant="filled" style={{ width: '110px' }}>
                    {firstFollowUpOptions.map(day => (
                      <Select.Option key={day} value={day}>
                        {day}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name={['followUp', 'second']}
                  label="How many days should I wait for the second follow-up?"
                >
                  <Select variant="filled" style={{ width: '110px' }}>
                    {secondFollowUpOptions.map(day => {
                      const valueFollowUpFirst = options.getFieldValue([
                        'followUp',
                        'first',
                      ])

                      const disabled =
                        valueFollowUpFirst !== null &&
                        day - valueFollowUpFirst < 2

                      return (
                        <Select.Option
                          key={day}
                          value={day}
                          disabled={disabled}
                        >
                          {day}
                        </Select.Option>
                      )
                    })}
                  </Select>
                </Form.Item>
              </>
            )
          }}
        </Form.Item>
      </CommonClient.Onboarding.Step>
    </>
  )
}
